/*
  Mouse and trackpad navigation for the product gallery on desktop.

  On mobile the media list is a scroll-snap slider, so a swipe already works.
  On desktop with a thumbnail layout it is not a scroll container at all — one
  media is shown and the rest are hidden — so the only way through the gallery
  was the thumbnail rail. This adds the two gestures a desktop shopper expects,
  and leaves the thumbnails doing exactly what they did.

  It drives media-gallery's own setActiveMedia, so the thumbnails, the counter,
  the dots and the live region all update through the path they already use.

  What it deliberately does NOT do: hijack a plain vertical wheel. That is the
  page scroll, and taking it over traps the reader in the gallery. Horizontal
  wheel (a trackpad two-finger swipe), shift+wheel and a click-drag are all
  unambiguous requests to move sideways; a downward flick is not.
*/
(function galleryPointerNav() {
  const DESKTOP = '(min-width: 750px)';
  // Far enough that a click on the image is never read as a drag.
  const DRAG_THRESHOLD = 40;
  // Wheel events arrive in bursts; one burst should move one slide.
  const WHEEL_COOLDOWN = 320;
  const WHEEL_THRESHOLD = 12;

  /*
    The slides are the <li>s, and only the <li>s.

    [data-media-id] alone is not enough: the inner <deferred-media> carries one
    too, and a different value — the slide's is prefixed with the section id,
    the media element's is the bare media id. Matching both interleaved the
    list, so navigating landed on an inner element, is-active went onto that,
    every <li> lost it, and the stage went blank.
  */
  function slides(gallery) {
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return [];
    // Shown slides only: the colour filter (.media-color-off) takes the other
    // colours out of the row, so counting them put the track on the wrong
    // picture — White selected, a gold photo showing.
    return Array.from(viewer.querySelectorAll('.product__media-item[data-media-id]')).filter(
      (slide) => !slide.classList.contains('media-color-off')
    );
  }

  function step(gallery, direction) {
    const list = slides(gallery);
    if (list.length < 2) return;

    let index = list.findIndex((slide) => slide.classList.contains('is-active'));
    if (index < 0) index = 0;

    const next = index + direction;
    // No wrapping: the ends of a product gallery should feel like ends, or a
    // shopper flicking through never learns they have seen everything.
    if (next < 0 || next >= list.length) return;

    const id = list[next].dataset.mediaId;
    if (id) gallery.setActiveMedia(id, false);
  }

  /*
    Moves the track to the active slide.

    Driven by a MutationObserver on the slides' class attribute rather than
    called from step(), because the active slide is changed from four places —
    the thumbnails, the dots, a variant change and this file. Watching the state
    means every one of them animates, and none of them has to know about the
    track.
  */
  function track(gallery) {
    const list = gallery.querySelector('[id^="Slider-Gallery"]');
    if (!list) return;

    const move = () => {
      const items = slides(gallery);
      const index = items.findIndex((slide) => slide.classList.contains('is-active'));
      if (index < 0) return;
      list.style.setProperty('--tyo-slide', String(index));
      arrowState(gallery, index, items.length);
    };

    move();

    new MutationObserver(move).observe(list, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  /*
    The desktop arrows on the main image (snippets/product-media-gallery.liquid).
    Dimmed at either end — no wrapping, same as step() — and hidden when only
    one picture is shown (e.g. a colour with a single photo).
  */
  function arrowState(gallery, index, count) {
    const arrows = gallery.querySelector('[data-gallery-arrows]');
    if (!arrows) return;
    arrows.hidden = count < 2;
    const prev = arrows.querySelector('[data-gallery-prev]');
    const next = arrows.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= count - 1;
  }

  function bind(gallery) {
    if (gallery.dataset.tyoPointerNav) return;
    gallery.dataset.tyoPointerNav = 'true';

    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return;

    // Arrow clicks take the same route as drag, wheel and keyboard.
    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => step(gallery, -1));
    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => step(gallery, 1));

    track(gallery);

    const desktop = window.matchMedia(DESKTOP);
    let lastWheel = 0;

    viewer.addEventListener(
      'wheel',
      (event) => {
        if (!desktop.matches) return;

        // Shift+wheel is the mouse-user's horizontal scroll; deltaX is the
        // trackpad's. Anything more vertical than horizontal is page scroll.
        const amount = event.shiftKey ? event.deltaY : event.deltaX;
        if (Math.abs(amount) < WHEEL_THRESHOLD) return;
        if (!event.shiftKey && Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

        const now = Date.now();
        if (now - lastWheel < WHEEL_COOLDOWN) {
          // Still the same burst. Swallow it so the page does not scroll
          // sideways underneath while we are ignoring it.
          event.preventDefault();
          return;
        }
        lastWheel = now;

        event.preventDefault();
        step(gallery, amount > 0 ? 1 : -1);
      },
      { passive: false }
    );

    /*
      Click-drag, for a plain mouse. Pointer events cover mouse, pen and touch
      with one path, and the touch case is filtered out below because mobile
      already has a real scroll slider.
    */
    let startX = null;
    let dragged = false;

    viewer.addEventListener('pointerdown', (event) => {
      /*
        Cleared FIRST, before any of the guards below.

        It used to be cleared at the end, after the early return for buttons —
        and the video's poster is a button. So a drag anywhere in the gallery
        left this true, the guard below skipped the reset, and the capture
        handler then swallowed the poster click. Clicking the video did
        nothing at all, for the rest of the page's life.
      */
      dragged = false;
      startX = null;

      if (!desktop.matches || event.pointerType === 'touch') return;
      if (event.button !== 0) return;
      // Let the real controls have their presses.
      if (event.target.closest('button, a, [data-sound-toggle]')) return;
      startX = event.clientX;
    });

    viewer.addEventListener('pointermove', (event) => {
      if (startX === null || dragged) return;
      const moved = event.clientX - startX;
      if (Math.abs(moved) < DRAG_THRESHOLD) return;
      dragged = true;
      step(gallery, moved < 0 ? 1 : -1);
    });

    const end = () => {
      startX = null;
    };
    viewer.addEventListener('pointerup', end);
    viewer.addEventListener('pointercancel', end);
    viewer.addEventListener('pointerleave', end);

    /*
      A drag that crossed the threshold started on an image, and that image is
      a link to the zoom modal — so the browser would fire a click at the end of
      it. Swallowed in the capture phase, before anything else sees it.
    */
    viewer.addEventListener(
      'click',
      (event) => {
        if (!dragged) return;
        dragged = false;
        /*
          Never swallow a control's click, whatever the drag state says. A
          stuck flag here is the difference between a stray click and a video
          that can never be started, and one of those is not recoverable by
          the shopper.
        */
        if (event.target.closest('button, a, [data-sound-toggle]')) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    // Left and right arrows, once the gallery has focus. Free to add and the
    // one thing keyboard users have no other route to here.
    viewer.addEventListener('keydown', (event) => {
      if (!desktop.matches) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.target.closest('input, textarea')) return;
      event.preventDefault();
      step(gallery, event.key === 'ArrowRight' ? 1 : -1);
    });
  }

  function start() {
    document.querySelectorAll('media-gallery').forEach((gallery) => {
      // The element upgrades on its own schedule; setActiveMedia is what we
      // need and it only exists once it has.
      if (typeof gallery.setActiveMedia === 'function') {
        bind(gallery);
      } else {
        customElements.whenDefined('media-gallery').then(() => bind(gallery));
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // A variant change re-renders the gallery, so re-bind what comes back.
  document.addEventListener('shopify:section:load', start);
  new MutationObserver(start).observe(document.body, { childList: true, subtree: true });
})();
